import { useState, useEffect } from 'react';
import { MessageCircle, Building2, Check, Tags, Link2, History, Trash2, RotateCcw, UserCheck } from 'lucide-react';
import toast from 'react-hot-toast';
import { Comments } from '@/features/comments/components/Comments';
import { AssignToClientModal } from './AssignToClientModal';
import { AssignToUserModal } from './AssignToUserModal';
import { SegmentEditorModal } from './SegmentEditorModal';
import { UsefulLinksPanel } from './UsefulLinksPanel';
import { VersionHistoryPanel } from './VersionHistoryPanel';
import { feedItemLinkApi } from '../services/feedItemLinkApi';
import { api } from '@/shared/services/api';
import { useFeedStore } from '@/app/store/feedStore';
import { BusinessSegment, FeedItemAssignmentDto, SegmentSource } from '@/shared/types/api';
import { cn } from '@/shared/utils/cn';

interface FeedItemActionsProps {
  feedItemId: number;
  currentUserId: string;
  isAdmin?: boolean;
  /** SC-238 Read Only tier: the user may view an item's assignment but not change it (SC-423). */
  isReadOnly?: boolean;
  isRead: boolean;
  businessSegments?: BusinessSegment[];
  businessSegmentsSource?: SegmentSource;
  tags?: string[];
  releaseType?: string | null;
  versionCount?: number;
  isDeleted?: boolean;
  /** SC-423: the item's user assignment, embedded in the feed row; null when unassigned. */
  assignment?: FeedItemAssignmentDto | null;
  onMarkAsRead: () => void;
}

export function FeedItemActions({
  feedItemId,
  currentUserId,
  isAdmin = false,
  isReadOnly = false,
  isRead,
  businessSegments,
  businessSegmentsSource,
  tags,
  releaseType,
  versionCount = 1,
  isDeleted = false,
  assignment = null,
  onMarkAsRead,
}: FeedItemActionsProps) {
  const updateItemDeleted = useFeedStore((state) => state.updateItemDeleted);
  const setItemAssignment = useFeedStore((state) => state.setItemAssignment);
  const [showAssignUserModal, setShowAssignUserModal] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showSegmentModal, setShowSegmentModal] = useState(false);
  const [showLinks, setShowLinks] = useState(false);
  const [showVersions, setShowVersions] = useState(false);
  const [commentCount, setCommentCount] = useState(0);
  const assignmentSummary = assignment
    ? `Assigned to ${assignment.assignees.map((a) => a.fullName).join(', ')} — review by ${assignment.reviewByDate}`
    : null;
  const [linkCount, setLinkCount] = useState(0);

  const handleToggleDeleted = async () => {
    // Archive, never erase: this only flips the deleted flag; the item and its history stay
    // in the system and can be restored (SC-278).
    const ok = await updateItemDeleted(feedItemId, !isDeleted);
    if (ok) toast.success(isDeleted ? 'Item restored' : 'Item archived (kept in the system)');
  };

  useEffect(() => {
    const loadCommentCount = async () => {
      try {
        const response = await api.get(`/comments/count/${feedItemId}`);
        setCommentCount(response.data.count);
      } catch (error) {
        console.error('Error loading comment count:', error);
      }
    };

    loadCommentCount();
  }, [feedItemId]);

  useEffect(() => {
    // Badge the useful-links icon so a reader can see an item has supporting context
    // without opening the popup (SC-275).
    const loadLinkCount = async () => {
      try {
        const links = await feedItemLinkApi.list(feedItemId);
        setLinkCount(links.length);
      } catch (error) {
        console.error('Error loading useful link count:', error);
      }
    };

    loadLinkCount();
  }, [feedItemId]);

  return (
    <div className="border-t border-border pt-3 mt-3">
      {/* Action Buttons Bar */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-1">
          {/* Comments Button */}
          <button
            onClick={() => setShowComments(!showComments)}
            className={cn('btn-icon relative', showComments ? 'text-primary bg-primary/10' : 'text-muted-foreground')}
            title="Comments"
            aria-label="Comments"
            aria-pressed={showComments}
          >
            <MessageCircle className="w-4 h-4" />
            {commentCount > 0 && (
              <span className="absolute -top-1 -right-1 min-w-5 h-5 px-1 flex items-center justify-center rounded-full bg-primary text-xs font-medium text-primary-foreground">
                {commentCount}
              </span>
            )}
          </button>

          {/* Assign to Client Button */}
          <button
            onClick={() => setShowAssignModal(true)}
            className="btn-icon text-muted-foreground"
            title="Assign to Client"
            aria-label="Assign to Client"
          >
            <Building2 className="w-4 h-4" />
          </button>

          {/* Assign to User Button (SC-423) — filled/primary when the item is assigned. Read Only
              users may not assign, so they get no button, only the assigned state when there is one. */}
          {!isReadOnly ? (
            <button
              onClick={() => setShowAssignUserModal(true)}
              className={cn('btn-icon', assignment ? 'text-primary' : 'text-muted-foreground')}
              title={assignmentSummary ?? 'Assign to User'}
              aria-label={assignmentSummary ? `Assign to User (${assignmentSummary})` : 'Assign to User'}
            >
              <UserCheck className={cn('w-4 h-4', assignment && 'fill-primary/20')} />
            </button>
          ) : (
            assignmentSummary && (
              <span
                className="inline-flex h-9 w-9 items-center justify-center text-primary"
                title={assignmentSummary}
                role="img"
                aria-label={assignmentSummary}
              >
                <UserCheck className="w-4 h-4 fill-primary/20" />
              </span>
            )
          )}

          {/* Update Metadata Button — segments + tags (SC-240) */}
          <button
            onClick={() => setShowSegmentModal(true)}
            className={cn(
              'btn-icon',
              businessSegments && businessSegments.length > 0
                ? 'text-primary'
                : 'text-muted-foreground'
            )}
            title={
              businessSegmentsSource === 'Ai'
                ? 'Update Metadata (segments AI-suggested)'
                : 'Update Metadata'
            }
            aria-label="Update Metadata"
          >
            <Tags className="w-4 h-4" />
          </button>

          {/* Useful Links Button (SC-237) — inline expandable panel like comments (SC-291) */}
          <button
            onClick={() => setShowLinks(!showLinks)}
            className={cn(
              'btn-icon relative',
              showLinks
                ? 'text-primary bg-primary/10'
                : linkCount > 0
                  ? 'text-primary'
                  : 'text-muted-foreground'
            )}
            title="Useful Links"
            aria-label="Useful Links"
            aria-pressed={showLinks}
          >
            <Link2 className="w-4 h-4" />
            {linkCount > 0 && (
              <span className="absolute -top-1 -right-1 min-w-5 h-5 px-1 flex items-center justify-center rounded-full bg-primary text-xs font-medium text-primary-foreground">
                {linkCount}
              </span>
            )}
          </button>

          {/* Version History Button (SC-278) — shown only for items that have been revised */}
          {versionCount > 1 && (
            <button
              onClick={() => setShowVersions(!showVersions)}
              className={cn('btn-icon relative', showVersions ? 'text-primary bg-primary/10' : 'text-primary')}
              title={`Version history (${versionCount} versions)`}
              aria-label="Version history"
              aria-pressed={showVersions}
            >
              <History className="w-4 h-4" />
              <span className="absolute -top-1 -right-1 min-w-5 h-5 px-1 flex items-center justify-center rounded-full bg-primary text-xs font-medium text-primary-foreground">
                {versionCount}
              </span>
            </button>
          )}

          {/* Mark deleted / restore — admin only (SC-278) */}
          {isAdmin && (
            <button
              onClick={handleToggleDeleted}
              className={cn('btn-icon', isDeleted ? 'text-primary' : 'text-muted-foreground')}
              title={isDeleted ? 'Restore item' : 'Archive item (marks deleted; kept in the system)'}
              aria-label={isDeleted ? 'Restore item' : 'Archive item'}
            >
              {isDeleted ? <RotateCcw className="w-4 h-4" /> : <Trash2 className="w-4 h-4" />}
            </button>
          )}
        </div>

        {/* Mark as Read */}
        {!isRead && (
          <button onClick={onMarkAsRead} className="btn-secondary btn-sm">
            <Check className="w-4 h-4" />
            Mark as Read
          </button>
        )}
      </div>

      {/* Version History Panel — inline (SC-278) */}
      {showVersions && (
        <div className="mt-4 pt-4 border-t border-border bg-muted/40 -mx-4 px-4 rounded-b-lg animate-fade-in">
          <VersionHistoryPanel feedItemId={feedItemId} />
        </div>
      )}

      {/* Useful Links Panel — inline, can be open alongside comments (SC-291) */}
      {showLinks && (
        <div className="mt-4 pt-4 border-t border-border bg-muted/40 -mx-4 px-4 rounded-b-lg animate-fade-in">
          <UsefulLinksPanel
            feedItemId={feedItemId}
            currentUserId={currentUserId}
            isAdmin={isAdmin}
            onCountChange={setLinkCount}
          />
        </div>
      )}

      {/* Comments Panel - Full Width */}
      {showComments && (
        <div className="mt-4 pt-4 border-t border-border bg-muted/40 -mx-4 px-4 rounded-b-lg animate-fade-in">
          <Comments
            feedItemId={feedItemId}
            currentUserId={currentUserId}
            isAdmin={isAdmin}
          />
        </div>
      )}

      {/* Assign to Client Modal */}
      {showAssignModal && (
        <AssignToClientModal
          feedItemId={feedItemId}
          onClose={() => setShowAssignModal(false)}
        />
      )}

      {/* Assign to User Modal (SC-423) */}
      {showAssignUserModal && !isReadOnly && (
        <AssignToUserModal
          feedItemId={feedItemId}
          currentAssignment={assignment}
          onClose={() => setShowAssignUserModal(false)}
          onSaved={(saved) => setItemAssignment(feedItemId, saved)}
        />
      )}

      {/* Update Metadata Modal (SC-240) */}
      {showSegmentModal && (
        <SegmentEditorModal
          feedItemId={feedItemId}
          currentSegments={businessSegments ?? []}
          currentTags={tags ?? []}
          currentReleaseType={releaseType ?? null}
          isAdmin={isAdmin}
          onClose={() => setShowSegmentModal(false)}
        />
      )}
    </div>
  );
}
